import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { LoadPanel } from "devextreme-react";
import Splitter, { Item as SplitterItem } from "devextreme-react/splitter";
import notify from "devextreme/ui/notify";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { CustomDataGrid } from "../CustomDataGrid";
import { Ticket, TicketStatus, TicketPriority, getTickets, isClosedStatus, TICKET_STATUS_DEFS } from "../../../api/support";
import { getCurrentUser } from "../../../api/auth";
import { onQueueEventName } from "../../../api/socket";
import { STATUS_TEXT_KEYS, STATUS_BADGE, TYPE_SHORT, getSlaInfo, formatSlaRemaining, isSlaRisk } from "./ticket-constants";
import { TicketDetailPane } from "./TicketDetailPane";
import NewTicketPage from "../new-ticket/NewTicketPage";
import { VendorInfoPane } from "../vendors/VendorInfoPane";
import "./TicketPage.css";

// ── Lookup tables (grid only) ────────────────────────────────────────────────

// Schema: 1=high, 2=medium, 3=low
const PRIORITY_TEXT_KEYS: Record<TicketPriority, string> = {
    1: "priorityHigh", 2: "priorityNormal", 3: "priorityLow",
};
const PRIORITY_TEXT_COLOR: Record<TicketPriority, string> = {
    1: "#EF4444", 2: "#F97316", 3: "#9CA3AF",
};

function formatDateShort(dateStr?: string) {
    if (!dateStr) return "—";
    try {
        return new Date(dateStr).toLocaleDateString("tr-TR", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return dateStr || "—";
    }
}

// ── Tab types ────────────────────────────────────────────────────────────────

type DetailTab = {
    ref: string;
    ticketId: string;   // "new"/"vendor" tab'larinda bos
    title: string;
    kind?: "detail" | "new" | "vendor"; // varsayilan "detail"
    vendorRef?: string; // yalnizca "vendor" tab'larinda
};

const createTabId = () => `dtab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// ── Main component ──────────────────────────────────────────────────────────

export default function TicketPage() {
    const { t } = useTranslation();
    // List state
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [filteredTickets, setFilteredTickets] = useState<Ticket[]>([]);
    const [loading, setLoading] = useState(true);
    const [gridKey, setGridKey] = useState(0);

    // Filter state
    const [statusFilter, setStatusFilter] = useState<string>("active");
    const [typeFilter, setTypeFilter] = useState<number | null>(null);       // ticketTypeNr
    const [assignedFilter, setAssignedFilter] = useState<string | null>(null); // assignedToRef ("0"=atanmamis)
    const [slaFilter, setSlaFilter] = useState<string | null>(null);          // "risk" => SLA riski (client-side)

    // Menu'den gelen query param'lara gore filtreleri sur; ticket listesi backend'den (6500) bu numaralarla cekilir.
    const [searchParams] = useSearchParams();
    useEffect(() => {
        const s = searchParams.get("status");
        const type = searchParams.get("ticketTypeNr");
        const assigned = searchParams.get("assignedToRef");
        const sla = searchParams.get("sla");

        setSlaFilter(sla === "risk" ? "risk" : null);
        setTypeFilter(type ? Number(type) : null);
        // assignedToRef=me -> oturumdaki kullanici ref'i; "0" -> atanmamis; yoksa filtre yok
        if (assigned === "me") setAssignedFilter(getCurrentUser()?.id ?? null);
        else setAssignedFilter(assigned ?? null);

        const sNr = Number(s);
        if (s === "active" || s === "passive" || s === "all") {
            setStatusFilter(s);
        } else if (s === "pending") {
            setStatusFilter("6"); // geriye donuk uyumluluk: Beklemede = PENDING
        } else if (s && Number.isInteger(sNr) && sNr >= 1 && sNr <= 15) {
            setStatusFilter(s); // dogrudan ticketStatusNr
        } else if (type || assigned) {
            // tip/atanan filtresi geldiyse durumu ayrica kisitlama, hepsini goster
            setStatusFilter("all");
        }
    }, [searchParams]);
    const [dateFrom, setDateFrom] = useState<Date | null>(new Date());
    const [dateTo, setDateTo] = useState<Date | null>(new Date());

    const [bottomCollapsed, setBottomCollapsed] = useState(false);
    const [topCollapsed, setTopCollapsed] = useState(false);

    const toggleBottom = useCallback(() => {
        setBottomCollapsed(prev => !prev);
        setTopCollapsed(false);
        setTimeout(() => setGridKey(k => k + 1), 250);
    }, []);

    const toggleTop = useCallback(() => {
        setTopCollapsed(prev => !prev);
        setBottomCollapsed(false);
        setTimeout(() => setGridKey(k => k + 1), 250);
    }, []);

    // MDI Tab state
    const [detailTabs, setDetailTabs] = useState<DetailTab[]>([]);
    const [activeTabId, setActiveTabId] = useState<string | null>(null);
    const [splitTabId, setSplitTabId] = useState<string | null>(null);

    const activeTab = detailTabs.find(t => t.ref === activeTabId) || null;
    const splitTab = splitTabId ? detailTabs.find(t => t.ref === splitTabId) || null : null;

    // ── Load ticket list ────────────────────────────────────────────────────
    const loadTickets = useCallback(async () => {
        setLoading(true);
        try {
            const params: { date_from?: string; date_to?: string; status?: number; ticket_type?: number; assigned_to?: string } = {};
            if (dateFrom) {
                const from = new Date(dateFrom);
                from.setHours(0, 0, 0, 0);
                params.date_from = from.toISOString();
            }
            if (dateTo) {
                const to = new Date(dateTo);
                to.setHours(23, 59, 59, 999);
                params.date_to = to.toISOString();
            }
            // statusFilter "1".."15" => o ticketStatusNr backend'e (6500) gonderilir.
            // active/passive/all => birden cok statu kapsadigi icin backend'e gonderilmez, client-side daraltilir.
            const statusNr = Number(statusFilter);
            if (Number.isInteger(statusNr) && statusNr >= 1 && statusNr <= 15) params.status = statusNr;
            // Menu'den gelen tip/atanan filtreleri backend'e (6500) ticketTypeNr / assignedToRef olarak gider.
            if (typeFilter != null) params.ticket_type = typeFilter;
            if (assignedFilter != null) params.assigned_to = assignedFilter;
            const data = await getTickets(params);
            setTickets(data);
        } catch {
            notify(t("ticketsLoadError"), "error", 2000);
        } finally {
            setLoading(false);
        }
    }, [dateFrom, dateTo, statusFilter, typeFilter, assignedFilter, t]);

    useEffect(() => {
        loadTickets();
    }, [loadTickets]);

    // ── Live updates via socket.io ──────────────────────────────────────────
    useEffect(() => {
        const off = onQueueEventName<{ event: string; ticket: Ticket }>(
            "queue.ticket.audit.log",
            ({ payload }) => {
                const tk = payload?.ticket;
                if (!tk || !tk.ref) return;
                setTickets(prev => {
                    const idx = prev.findIndex(x => x.ref === tk.ref);
                    if (idx === -1) return [tk, ...prev];
                    const copy = [...prev];
                    copy[idx] = { ...copy[idx], ...tk };
                    return copy;
                });
            },
        );
        return off;
    }, []);

    // ── Status filter (client-side; tarih backend'de filtreleniyor) ─────────
    useEffect(() => {
        let filtered = [...tickets];
        const statusNr = Number(statusFilter);
        if (statusFilter === "active") {
            filtered = filtered.filter(tk => !isClosedStatus(tk.status));
        } else if (statusFilter === "passive") {
            filtered = filtered.filter(tk => isClosedStatus(tk.status));
        } else if (Number.isInteger(statusNr) && statusNr >= 1 && statusNr <= 15) {
            filtered = filtered.filter(tk => tk.status === statusNr);
        }
        // SLA riski (due_date'ten turetilir, backend filtresi degil) — client-side daralt.
        if (slaFilter === "risk") {
            filtered = filtered.filter(tk => isSlaRisk(tk));
        }
        setFilteredTickets(filtered);
    }, [tickets, statusFilter, slaFilter]);

    // ── Tab management ───────────────────────────────────────────────────────
    const openTicketInTab = useCallback((ticket: Ticket) => {
        setDetailTabs(prev => {
            const existing = prev.find(t => t.ticketId === ticket.ref);
            if (existing) {
                setActiveTabId(existing.ref);
                return prev;
            }
            const tabId = createTabId();
            setActiveTabId(tabId);
            return [...prev, {
                ref: tabId,
                ticketId: ticket.ref,
                title: ticket.ticket_code || ticket.title?.slice(0, 20) || "Ticket",
            }];
        });
    }, []);

    // Yeni ticket formunu yeni sayfaya gitmek yerine alt panelde bir tab olarak ac.
    const openNewTicketTab = useCallback(() => {
        setDetailTabs(prev => {
            // Tek bir "yeni ticket" tab'i yeter; zaten acilsa ona gec.
            const existing = prev.find(t => t.kind === "new");
            if (existing) {
                setActiveTabId(existing.ref);
                return prev;
            }
            const tabId = createTabId();
            setActiveTabId(tabId);
            return [...prev, { ref: tabId, ticketId: "", title: t("new-ticket"), kind: "new" }];
        });
    }, [t]);

    // Vendor (musteri firma) bilgi tab'ini ac; ayni vendor zaten acilsa ona gec.
    const openVendorTab = useCallback((vendorRef: string, vendorName?: string) => {
        if (!vendorRef) return;
        setDetailTabs(prev => {
            const existing = prev.find(t => t.kind === "vendor" && t.vendorRef === vendorRef);
            if (existing) {
                setActiveTabId(existing.ref);
                return prev;
            }
            const tabId = createTabId();
            setActiveTabId(tabId);
            return [...prev, {
                ref: tabId,
                ticketId: "",
                vendorRef,
                title: vendorName || `Vendor #${vendorRef}`,
                kind: "vendor",
            }];
        });
    }, []);

    const closeDetailTab = useCallback((tabId: string) => {
        setDetailTabs(prev => {
            if (prev.length <= 1) {
                setActiveTabId(null);
                setSplitTabId(null);
                return [];
            }
            const idx = prev.findIndex(t => t.ref === tabId);
            const next = prev.filter(t => t.ref !== tabId);
            if (splitTabId === tabId) setSplitTabId(null);
            if (activeTabId === tabId) {
                const fallback = next[Math.max(0, idx - 1)] || next[0];
                setActiveTabId(fallback?.ref || null);
            }
            return next;
        });
    }, [activeTabId, splitTabId]);

    const openTicketInSplitView = useCallback((ticket: Ticket) => {
        setDetailTabs(prev => {
            const existing = prev.find(t => t.ticketId === ticket.ref);
            if (existing) {
                if (existing.ref === activeTabId) return prev;
                setSplitTabId(existing.ref);
                return prev;
            }
            const tabId = createTabId();
            setSplitTabId(tabId);
            return [...prev, {
                ref: tabId,
                ticketId: ticket.ref,
                title: ticket.ticket_code || ticket.title?.slice(0, 20) || "Ticket",
            }];
        });
    }, [activeTabId]);

    // Tab icerigi: "new" => gomulu yeni-ticket formu, aksi halde ticket detayi.
    const renderTabContent = useCallback((tab: DetailTab) => {
        if (tab.kind === "new") {
            return (
                <NewTicketPage
                    embedded
                    onCancel={() => closeDetailTab(tab.ref)}
                    onCreated={() => { closeDetailTab(tab.ref); loadTickets(); }}
                    onOpenVendor={openVendorTab}
                />
            );
        }
        if (tab.kind === "vendor") {
            return <VendorInfoPane vendorRef={tab.vendorRef ?? ""} />;
        }
        return <TicketDetailPane ticketId={tab.ticketId} />;
    }, [closeDetailTab, loadTickets, openVendorTab]);

    // ── Context menu ────────────────────────────────────────────────────────
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; ticket: Ticket } | null>(null);
    const contextMenuRef = useRef<HTMLDivElement>(null);

    const handleRowContextMenu = useCallback((e: React.MouseEvent, ticket: Ticket) => {
        e.preventDefault();
        setContextMenu({ x: e.clientX, y: e.clientY, ticket });
    }, []);

    useEffect(() => {
        if (!contextMenu || !contextMenuRef.current) return;
        const menu = contextMenuRef.current;
        const rect = menu.getBoundingClientRect();
        let { x, y } = contextMenu;
        let adjusted = false;

        if (x + rect.width > window.innerWidth) {
            x = window.innerWidth - rect.width - 8;
            adjusted = true;
        }
        if (y + rect.height > window.innerHeight) {
            y = window.innerHeight - rect.height - 8;
            adjusted = true;
        }
        if (x < 0) { x = 8; adjusted = true; }
        if (y < 0) { y = 8; adjusted = true; }

        if (adjusted) {
            setContextMenu(prev => prev ? { ...prev, x, y } : null);
        }
    }, [contextMenu]);

    useEffect(() => {
        if (!contextMenu) return;
        const close = (e: MouseEvent) => {
            if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
                setContextMenu(null);
            }
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [contextMenu]);

    // ── Row select handler (click + keyboard navigation) ──────────────────
    const selectTicket = useCallback((data: Ticket) => {
        if (!activeTabId || detailTabs.length === 0) {
            openTicketInTab(data);
            return;
        }
        setDetailTabs(prev => prev.map(tab =>
            tab.ref === activeTabId
                ? { ...tab, ticketId: data.ref, title: data.ticket_code || data.title?.slice(0, 20) || "Ticket" }
                : tab
        ));
    }, [activeTabId, detailTabs.length, openTicketInTab]);

    const handleRowClick = (data: Ticket) => selectTicket(data);

    const handleFocusedRowChanged = useCallback((e: any) => {
        if (e?.row?.data) {
            selectTicket(e.row.data);
        }
    }, [selectTicket]);

    // ── Grid columns ────────────────────────────────────────────────────────
    const columns = [
        {
            dataField: "status",
            caption: t("status"),
            width: 140,
            alignment: "left" as const,
            cellRender: (data: any) => {
                const color = STATUS_BADGE[data.value as TicketStatus];
                return (
                    <div className="text-left">
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            background: `${color}1f`, color, fontWeight: 600, fontSize: 12,
                            padding: '2px 10px', borderRadius: 999, whiteSpace: 'nowrap',
                        }}>
                            <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: color }} />
                            {t(STATUS_TEXT_KEYS[data.value as TicketStatus])}
                        </span>
                    </div>
                );
            },
        },
        {
            name: "sla",
            caption: t("sla"),
            width: 130,
            alignment: "left" as const,
            // Gorsel SLA gostergesi; siralama icin "Son Tarih" (due_date) kolonu kullanilir.
            cellRender: (data: any) => {
                const sla = getSlaInfo(data.data as Ticket);
                if (sla.state === "none") {
                    return <div className="text-left"><span className="text-xs" style={{ color: 'var(--subtitle-text-color)' }}>—</span></div>;
                }
                const remaining = formatSlaRemaining(sla.remainingMs as number, t);
                const label = sla.state === "overdue"
                    ? t("slaOverdueBy", { time: remaining })
                    : t("slaRemaining", { time: remaining });
                return (
                    <div className="text-left">
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            background: `${sla.color}1f`, color: sla.color, fontWeight: 600, fontSize: 11,
                            padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap',
                        }}>
                            <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: sla.color }} />
                            {label}
                        </span>
                    </div>
                );
            },
        },
        {
            dataField: "ticket_code",
            caption: t("ticketNo"),
            width: 150,
            alignment: "left" as const,
            cellRender: (data: any) => (
                <div className="text-left"><span className="font-mono text-xs" style={{ color: 'var(--subtitle-text-color)' }}>{data.value}</span></div>
            ),
        },
        {
            dataField: "customer_name",
            caption: t("customer"),
            width: 160,
            alignment: "left" as const,
            cellRender: (data: any) => (
                <div className="text-left"><span style={{ color: 'var(--base-text-color)' }}>{data.value}</span></div>
            ),
        },
        {
            dataField: "title",
            caption: t("title"),
            width: 300,
            alignment: "left" as const,
            cellRender: (data: any) => (
                <div className="text-left"><span style={{ color: 'var(--base-text-color)' }}>{data.value}</span></div>
            ),
        },
        {
            dataField: "type_name",
            caption: t("ticketType"),
            width: 100,
            alignment: "left" as const,
            cellRender: (data: any) => {
                const full = data.value || "";
                const short = TYPE_SHORT[data.data.ticket_type as number]
                    || (full ? String(full).slice(0, 4).toUpperCase() : "—");
                return (
                    <div className="text-left" title={full}>
                        <span style={{
                            display: 'inline-block', fontFamily: 'monospace', fontWeight: 700,
                            fontSize: 11, letterSpacing: '0.04em', padding: '2px 8px', borderRadius: 6,
                            background: 'var(--base-bg-darken-4)', color: 'var(--base-text-color)',
                        }}>
                            {short}
                        </span>
                    </div>
                );
            },
        },
        {
            dataField: "priority",
            caption: t("priority"),
            width: 90,
            alignment: "left" as const,
            cellRender: (data: any) => (
                <span style={{ color: PRIORITY_TEXT_COLOR[data.value as TicketPriority] }}>
                    {t(PRIORITY_TEXT_KEYS[data.value as TicketPriority])}
                </span>
            ),
        },
        {
            dataField: "assigned_to_name",
            caption: t("assignedTo"),
            width: 130,
            alignment: "left" as const,
            cellRender: (data: any) => (
                <div className="text-left"><span style={{ color: 'var(--base-text-color)' }}>{data.value || "—"}</span></div>
            ),
        },
        {
            dataField: "support_group_name",
            caption: t("supportGroup"),
            width: 150,
            alignment: "left" as const,
            cellRender: (data: any) => (
                <div className="text-left"><span style={{ color: 'var(--base-text-color)' }}>{data.value || "—"}</span></div>
            ),
        },
        {
            dataField: "customer_email",
            caption: t("contactEmail"),
            width: 180,
            alignment: "left" as const,
            cellRender: (data: any) => (
                <div className="text-left"><span className="text-xs" style={{ color: 'var(--base-text-color-alpha)' }}>{data.value || "—"}</span></div>
            ),
        },
        {
            dataField: "author_name",
            caption: t("createdBy"),
            width: 130,
            alignment: "left" as const,
            cellRender: (data: any) => (
                <div className="text-left"><span style={{ color: 'var(--base-text-color)' }}>{data.value || "—"}</span></div>
            ),
        },
        {
            dataField: "created_at",
            caption: t("createdAt"),
            width: 140,
            alignment: "right" as const,
            dataType: "datetime" as const,
            cellRender: (data: any) => (
                <div className="text-right"><span className="text-xs" style={{ color: 'var(--subtitle-text-color)' }}>{formatDateShort(data.value)}</span></div>
            ),
        },
        {
            dataField: "due_date",
            caption: t("dueDate"),
            width: 140,
            alignment: "right" as const,
            dataType: "datetime" as const,
            cellRender: (data: any) => (
                <div className="text-right"><span className="text-xs" style={{ color: 'var(--subtitle-text-color)' }}>{data.value ? formatDateShort(data.value) : "—"}</span></div>
            ),
        },
        {
            dataField: "",
            caption: "",
            width: undefined,
        },
        {
            dataField: "_delete",
            caption: "",
            width: 50,
            alignment: "center" as const,
            allowFiltering: false,
            allowSorting: false,
            cellRender: (data: any) => (
                <button
                    type="button"
                    className="ticket-row-delete-btn"
                    title={t("deleteBtn")}
                    onClick={(e) => {
                        e.stopPropagation();
                        // TODO: delete handler
                    }}
                >
                    <i className="dx-icon-trash" />
                </button>
            ),
        },
    ];

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="ticket-page-root flex flex-col h-full w-full" style={{ paddingBottom: 30 }}>
            <LoadPanel visible={loading} />

            <div className="ticket-splitter-wrap flex-1 min-h-0">
                <Splitter orientation="vertical">
                    {/* Top Pane - Ticket List */}
                    <SplitterItem size="40%" minSize="200px" collapsed={topCollapsed}>
                        <CustomDataGrid
                            key={`tickets-grid-${gridKey}`}
                            data={filteredTickets}
                            columns={columns}
                            keyExpr="ref"
                            height="100%"
                            onRefresh={loadTickets}
                            onRowClick={handleRowClick}
                            onRowContextMenu={handleRowContextMenu}
                            focusedRowKey={activeTab?.ticketId || null}
                            noDataText={t("noTicketsFound")}
                            columnChooserEnabled={true}
                            filterRowVisible={false}
                            rowAlternationEnabled={false}
                            allowColumnReordering={true}
                            onFocusedRowChanged={handleFocusedRowChanged}
                            statusFilterText={(() => {
                                if (statusFilter === "active") return t("active");
                                if (statusFilter === "passive") return t("passive");
                                if (statusFilter === "all") return t("all");
                                const def = TICKET_STATUS_DEFS.find(d => String(d.nr) === statusFilter);
                                return def ? t(def.labelKey) : t("all");
                            })()}
                            statusFilterOptions={[
                                { id: 0, text: t("all") },
                                { id: -1, text: t("active") },
                                { id: -2, text: t("passive") },
                                ...TICKET_STATUS_DEFS.map(d => ({ id: d.nr, text: t(d.labelKey) })),
                            ]}
                            onStatusFilter={(status: string) => {
                                if (status === t("active")) setStatusFilter("active");
                                else if (status === t("passive")) setStatusFilter("passive");
                                else if (status === t("all")) setStatusFilter("all");
                                else {
                                    const def = TICKET_STATUS_DEFS.find(d => t(d.labelKey) === status);
                                    setStatusFilter(def ? String(def.nr) : "all");
                                }
                            }}
                            dateFilters={[
                                {
                                    value: dateFrom,
                                    onValueChanged: setDateFrom,
                                },
                                {
                                    value: dateTo,
                                    onValueChanged: setDateTo,
                                },
                            ]}
                            onAddNew={openNewTicketTab}
                            additionalToolbarItems={[
                                {
                                    location: 'after',
                                    render: () => (
                                        <button
                                            type="button"
                                            className="splitter-collapse-btn"
                                            title={t("closeBottomPane")}
                                            onClick={toggleBottom}
                                        >
                                            <FontAwesomeIcon icon={bottomCollapsed ? faChevronUp : faChevronDown} />
                                        </button>
                                    ),
                                },
                            ]}
                        />
                    </SplitterItem>

                    {/* Bottom Pane - Tabbed Ticket Detail */}
                    <SplitterItem size="60%" minSize="300px" collapsed={bottomCollapsed}>
                        <div className="h-full flex flex-col overflow-hidden" style={{ background: 'var(--background-color)', padding: '0 12px' }}>
                            {detailTabs.length > 0 ? (
                                <>
                                    {/* Tab Bar */}
                                    <div className="detail-header-tabs shrink-0">
                                        {[...detailTabs.filter(t => t.ref !== splitTabId), ...detailTabs.filter(t => t.ref === splitTabId)].map(tab => (
                                            <div
                                                key={tab.ref}
                                                className={`detail-header-tab ${tab.ref === activeTabId ? 'active' : ''} ${tab.ref === splitTabId ? 'split' : ''}`}
                                                title={tab.title}
                                                onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); closeDetailTab(tab.ref); } }}
                                            >
                                                <button
                                                    type="button"
                                                    className="detail-header-tab-label"
                                                    onClick={() => setActiveTabId(tab.ref)}
                                                >
                                                    <i className={`${tab.kind === "new" ? "dx-icon-add" : tab.kind === "vendor" ? "dx-icon-group" : "dx-icon-comment"} detail-header-tab-icon`} />
                                                    <span className="detail-header-tab-title-text">{tab.title}</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="detail-header-tab-close"
                                                    onClick={(e) => { e.stopPropagation(); closeDetailTab(tab.ref); }}
                                                    title={t("closeTab")}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            className="splitter-collapse-btn"
                                            style={{ marginLeft: 'auto' }}
                                            title={t("closeTopPane")}
                                            onClick={toggleTop}
                                        >
                                            <FontAwesomeIcon icon={topCollapsed ? faChevronDown : faChevronUp} />
                                        </button>
                                    </div>

                                    {/* Tab Content */}
                                    <div className="flex-1 min-h-0">
                                        {splitTab && activeTab ? (
                                            <Splitter height="100%" width="100%">
                                                <SplitterItem size="50%" minSize="320px">
                                                    <div className="split-pane-card">
                                                        <div className="split-pane-header">
                                                            <i className="dx-icon-comment" />
                                                            <span>{activeTab.title}</span>
                                                        </div>
                                                        <div className="split-pane-body">
                                                            {renderTabContent(activeTab)}
                                                        </div>
                                                    </div>
                                                </SplitterItem>
                                                <SplitterItem size="50%" minSize="320px">
                                                    <div className="split-pane-card">
                                                        <div className="split-pane-header">
                                                            <i className="dx-icon-comment" />
                                                            <span>{splitTab.title}</span>
                                                        </div>
                                                        <div className="split-pane-body">
                                                            {renderTabContent(splitTab)}
                                                        </div>
                                                    </div>
                                                </SplitterItem>
                                            </Splitter>
                                        ) : (
                                            detailTabs.map(tab => (
                                                <div
                                                    key={tab.ref}
                                                    className="h-full"
                                                    style={{ display: tab.ref === activeTabId ? 'block' : 'none' }}
                                                >
                                                    {renderTabContent(tab)}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="detail-header-tabs shrink-0">
                                        <button
                                            type="button"
                                            className="splitter-collapse-btn"
                                            style={{ marginLeft: 'auto' }}
                                            title={t("closeTopPane")}
                                            onClick={toggleTop}
                                        >
                                            <FontAwesomeIcon icon={topCollapsed ? faChevronDown : faChevronUp} />
                                        </button>
                                    </div>
                                    <div className="flex items-center justify-center flex-1" style={{ color: 'var(--subtitle-text-color)' }}>
                                        {t("selectTicketForDetail")}
                                    </div>
                                </>
                            )}
                        </div>
                    </SplitterItem>
                </Splitter>
            </div>

            {/* Context Menu */}
            {contextMenu && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setContextMenu(null)} />
                    <div
                        ref={contextMenuRef}
                        className="fixed z-50 ticket-context-menu"
                        style={{ left: contextMenu.x, top: contextMenu.y }}
                    >
                        <button
                            className="ticket-context-menu-item"
                            onClick={() => { openTicketInTab(contextMenu.ticket); setContextMenu(null); }}
                        >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                            {t("openInNewTab")}
                        </button>
                        <button
                            className="ticket-context-menu-item"
                            onClick={() => { openTicketInSplitView(contextMenu.ticket); setContextMenu(null); }}
                        >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" /></svg>
                            {t("openInSplitView")}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
